import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api, authApi, ApiError } from '@/lib/api'
import { toRole, type CurrentUser, type Role, type UserDto } from '@/features/auth/types'

export const sessionQueryKey = ['session'] as const

function normalise(dto: UserDto): CurrentUser | null {
  const role = toRole(dto.role)
  // A member reaching this console is not a session we can use — treat it as
  // signed out rather than rendering a console with no permissions.
  if (!role) return null

  return {
    id: dto.id,
    displayName: dto.display_name,
    avatarUrl: dto.avatar_url,
    email: dto.identities[0]?.email ?? '',
    role,
  }
}

/**
 * Resolves the signed-in staff member from the session cookie. A 401 (no
 * session) or 403 (valid session, but the account has no staff role — a
 * consumer who hasn't been granted one yet) are both normal answers here,
 * not error states — from the SPA's perspective both just mean "no usable
 * console session." Exported (not just used inline in useSession) so
 * confirm-page.tsx can force a fresh fetch via queryClient.fetchQuery with
 * the same queryFn, right after the OAuth round trip sets a new cookie.
 */
export async function fetchSession(): Promise<CurrentUser | null> {
  try {
    return normalise(await api.get<UserDto>('/admin/me'))
  } catch (error) {
    if (error instanceof ApiError && (error.isUnauthorized || error.isForbidden)) return null
    throw error
  }
}

export function useSession() {
  const query = useQuery({
    queryKey: sessionQueryKey,
    queryFn: fetchSession,
    staleTime: 5 * 60_000,
  })

  return {
    user: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
  }
}

export function useHasRole(...roles: Role[]) {
  const { user } = useSession()
  return user != null && roles.includes(user.role)
}

/**
 * Ends the whole session, not just this console — /auth/logout destroys the
 * shared cwc_session, so cwc-frontend is signed out too. That's intentional:
 * this is a deliberate click, not the background admin-timeout check, and
 * "sign out" meaning a full sign-out is the expected behavior.
 */
export function useLogout() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: () => authApi.post('/auth/logout'),
    onSettled: () => {
      queryClient.setQueryData(sessionQueryKey, null)
      navigate('/login', { replace: true })
    },
  })
}
